import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Route, RouteParams } from '@core/routes/Route';
import { replaceRouteParam } from '@core/utils/string';
import { SurveyDetailsItem, useGetSurveyDetails } from '@core/api/useSurveys';
import { SurveyStatusIdEnum } from '@core/hooks/useCommonEnums';
import { useSaveSurveyItems, useSurveyAvailableItems } from '@core/api/useSurveyItems';
import { toast } from "react-toastify";
import { isDateInRange } from '@core/utils/date';

type EnhancedSurveyItem = SurveyDetailsItem & {
    isNew?: boolean;
    isDeleted?: boolean;
    insertedAt?: number;
};

const useSurveyAddItems = () => {
    // itemStates tracks the current state of all items that have been interacted with:
    // - Items that were originally included in the survey
    // - Items that have been newly added to included items - isNew
    // - Items that have been removed - isDeleted
    // - When items are newly added to included items, insertedAt is set to the current timestamp
    // The Map key is the item ID and the value is the enhanced item with state flags
    const [itemStates, setItemStates] = useState<Map<number, EnhancedSurveyItem>>(new Map());

    const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf('day'));
    const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(1, 'year').startOf('day'));

    const navigate = useNavigate();
    const { id: surveyId } = useParams();
    const { doFetch: saveSurveyItems } = useSaveSurveyItems();
    const {
        data: surveyData,
        doFetch: fetchSurveyDetails
    } = useGetSurveyDetails(surveyId, true);

    // Set of original item IDs for O(1) lookups
    const originalItemIds = useMemo((): Set<number> => {
        return new Set(surveyData?.items?.map((item) => item.id as number) ?? []);
    }, [surveyData?.items]);

    const onNavigateToSurveyDetails = useCallback(
        () => navigate(replaceRouteParam(Route.SurveyDetails, RouteParams.Id, surveyId ?? '')),
        [navigate, surveyId],
    );

    const onIncludeNewItem = useCallback((items?: SurveyDetailsItem[]) => {
        setItemStates((prev) => {
            const next = new Map(prev);

            for (const item of items ?? []) {
                const itemId = item?.id;
                if (typeof itemId !== 'number') continue;

                const isExistingItem = originalItemIds.has(itemId);
                next.set(itemId, {
                    ...item,
                    isNew: !isExistingItem,
                    isDeleted: false,
                    insertedAt: !isExistingItem ? Date.now() : undefined
                });
            }

            return next;
        });
    }, [originalItemIds]);

    const onRemoveIncludedItem = useCallback((items?: SurveyDetailsItem[]) => {

        setItemStates((prev) => {
            const next = new Map(prev);

            for (const item of items ?? []) {
                const itemId = item?.id;
                if (typeof itemId !== 'number') continue;

                next.set(itemId, { ...item, isNew: false, isDeleted: true });
            }

            return next;
        });
    }, []);

    const {
        data: availableItemsData,
        doFetch: fetchAvailableItems,
        isLoading: isAvailableItemsLoading,
    } = useSurveyAvailableItems();

    // Initialize itemStates with survey items when survey data is loaded
    useEffect(() => {
        if (surveyData?.items?.length) {
            setItemStates(new Map(
                surveyData.items.map(item => [
                    item.id as number,
                    { ...item, isNew: false, isDeleted: false }
                ])
            ));
        }
    }, [surveyData?.items]);

    useEffect(() => {
        fetchSurveyDetails();
    }, [fetchSurveyDetails]);

    useEffect(() => {
        if (!surveyData?.countryId) return;
        fetchAvailableItems({
            data: {
                startDate: startDate.format('YYYY-MM-DD'),
                endDate: endDate.format('YYYY-MM-DD'),
                countryId: surveyData?.countryId,
            },
        });
    }, [endDate, fetchAvailableItems, startDate, surveyData?.countryId]);

    useEffect(() => {
        if (surveyData?.status !== undefined && surveyData.status !== SurveyStatusIdEnum.Draft) {
            toast.error('Only Draft surveys can add new Items');
            onNavigateToSurveyDetails();
        }
    }, [surveyData?.status, onNavigateToSurveyDetails]);

    // Get items that should appear in the included table
    const includedItems = useMemo(() => {
        return Array.from(itemStates.values())
            .filter(item => !item.isDeleted)
            .sort((a, b) => {
                // If both items are new, sort by insertedAt
                if (a.isNew && b.isNew) {
                    return (a.insertedAt ?? 0) - (b.insertedAt ?? 0);
                }
                // If only one is new, put new items at the bottom
                if (a.isNew !== b.isNew) {
                    return a.isNew ? 1 : -1;
                }
                // For existing items, maintain original order by ID
                return (a.id ?? 0) - (b.id ?? 0);
            });
    }, [itemStates]);

    const apiAndLocalItems = useMemo((): EnhancedSurveyItem[] => [
        // Include API items that aren't already in itemStates (whether included or deleted)
        ...(availableItemsData ?? []).filter((item) => {
            if (!item.id) return false;
            const itemState = itemStates.get(item.id);
            return !itemState;
        }),
        // Include deleted items from itemStates that are within the date range
        ...Array.from(itemStates.values()).filter((item) => {
            if (!item.isDeleted) return false;
            // Only check the date range for deleted items since they might need to go back to available
            return isDateInRange(item.date, startDate, endDate);
        })
    ], [availableItemsData, itemStates, startDate, endDate]);

    const availableItems = useMemo((): EnhancedSurveyItem[] => apiAndLocalItems, [apiAndLocalItems]);

    const onSaveIncludedItems = useCallback(() => {
        if (surveyId) {
            saveSurveyItems({
                data: {
                    surveyId: parseInt(surveyId),
                    selectedItems: includedItems.flatMap((i) => i?.id ?? []),
                },
            })
                .then((res) => {
                    if (res?.status === 200) {
                        toast.success('Items saved successfully');
                        return onNavigateToSurveyDetails();
                    }

                    toast.error('Failed to save items');
                })
                .catch(() => {
                    toast.error('Failed to save items');
                });
        }
    }, [includedItems, onNavigateToSurveyDetails, saveSurveyItems, surveyId]);

    return {
        filters: {
            endDate,
            setEndDate,
            startDate,
            setStartDate,
        },
        actions: {
            onNavigateToSurveyDetails,
            onIncludeNewItem,
            onRemoveIncludedItem,
            onSaveIncludedItems,
        },
        data: {
            surveyData,
            availableItems,
            includedItems,
            newIncludedItems: includedItems.filter(item => item.isNew),
        },
        isAvailableItemsLoading,
    };
};

export default useSurveyAddItems;
